import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronDown, LogOut, User, Shield, AlertCircle, Menu, X, Compass,
  Building2, FileText, MapPin, Map, TriangleAlert, Image as ImageIcon, Ticket, BarChart3,
  ShieldCheck
} from 'lucide-react';
import { useAuth } from '../../authentication_components/authContext';

export default function HeaderBar({ activeTab, onTabChange }) {
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [submissionsDropdownOpen, setSubmissionsDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const { user, loginType, logout } = useAuth();
  const navigate = useNavigate();
  const navToPortal = () => navigate('/portal');

  // Main navigation items (non-dropdown)
  const mainNavItems = [
    { 
      id: 'registered_businesses', 
      label: 'Negocios Registrados', 
      icon: <Building2 className="w-4 h-4" /> 
    },
    { 
      id: 'business_reports', 
      label: 'Reportes', 
      icon: <TriangleAlert className="w-4 h-4" /> 
    },
    {
      id: 'route_moderation',
      label: 'Moderación',
      icon: <ShieldCheck className="w-4 h-4" />
    },
    {
      id: 'content_manager',
      label: 'Gestor de Contenido',
      icon: <FileText className="w-4 h-4" />
    },
    {
      id: 'viator_experiences',
      label: 'Experiencias Viator',
      icon: <Ticket className="w-4 h-4" />
    },
    {
      id: 'usage_metrics',
      label: 'Métricas de Uso',
      icon: <BarChart3 className="w-4 h-4" />
    }
  ];

  // Submissions dropdown items
  const submissionsItems = [
    { 
      id: 'location_submissions', 
      label: 'Sugerencias de Ubicación', 
      icon: <MapPin className="w-4 h-4" /> 
    },
    { 
      id: 'gallery_submissions', 
      label: 'Imágenes de Galería', 
      icon: <ImageIcon className="w-4 h-4" /> 
    },
    { 
      id: 'business_submissions', 
      label: 'Sugerencias de Negocios', 
      icon: <Map className="w-4 h-4" /> 
    },
    {
      id:'nature_meta',
      label:'detalles de aventuras',
      icon:<Compass className="w-4 h-4"/>
    }
  ];

  // Check if active tab is in submissions
  const isSubmissionsActive = submissionsItems.some(item => item.id === activeTab);

  const handleLogout = async () => {
    try {
      setLoggingOut(true);
      setUserDropdownOpen(false);
      setMobileMenuOpen(false);
      setSubmissionsDropdownOpen(false);
      
      await logout();
      
      console.log('Logout successful');
    } catch (error) {
      console.error('Logout error:', error);
      await logout();
    } finally {
      setLoggingOut(false);
    }
  };

  // Get user display info
  const getUserDisplayInfo = () => {
    if (!user) return { email: 'Usuario', lastLogin: 'N/A' };
    
    const email = loginType === 'master' ? 'Master User' : user.email || 'Admin User';
    // Safe date formatting
    const lastLogin = user.expiresAt 
      ? new Date(user.expiresAt * 1000).toLocaleDateString('es-PR', {
          month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
        })
      : 'Sesión actual';
    
    return { email, lastLogin };
  };

  const { email, lastLogin } = getUserDisplayInfo();

  // Get appropriate icon and styling based on login type
  const getUserIcon = () => {
    if (loginType === 'master') {
      return (
        <div className="w-8 h-8 bg-gradient-to-r from-red-600 to-rose-700 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
          <Shield className="h-4 w-4 text-white" />
        </div>
      );
    }
    
    return (
      <div className="w-8 h-8 bg-gradient-to-r from-violet-600 to-indigo-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
        <User className="h-4 w-4 text-white" />
      </div>
    );
  };

  const getUserTypeLabel = () => {
    return loginType === 'master' ? 'Master Administrator' : 'Administrator';
  };

  const handleTabChange = (tabId) => {
    onTabChange(tabId);
    setMobileMenuOpen(false);
    setSubmissionsDropdownOpen(false);
  };

  const getActiveSubmissionLabel = () => {
    const activeItem = submissionsItems.find(item => item.id === activeTab);
    return activeItem ? activeItem.label : 'Sugerencias';
  };

  return (
    <header className="bg-white shadow-md border-b border-slate-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
        <div className="flex justify-between items-center h-16">
          
          {/* Logo Section */}
          <div className="flex flex-start items-center min-w-0 flex-shrink-0 gap-2">
            <h1
            onClick={navToPortal} 
            className="text-lg sm:text-xl font-bold bg-gradient-to-r from-slate-800 to-black bg-clip-text text-transparent cursor-pointer truncate tracking-tight">
              PR LocaL
            </h1>
            {loginType === 'master' && (
              <span className="ml-1 sm:ml-2 px-2 py-0.5 bg-red-50 text-red-700 text-[10px] sm:text-xs font-bold rounded-full border border-red-100 uppercase tracking-wider">
                Master
              </span>
            )}
          </div>

          {/* Desktop Navigation Tabs - Hidden on mobile/tablet */}
          <div className="hidden xl:flex space-x-1 flex-1 justify-center mx-4">
            {/* Main Nav Items */}
            {mainNavItems.map((item) => {
              const isActive = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => onTabChange(item.id)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-2 whitespace-nowrap ${
                    isActive
                      ? 'bg-violet-50 text-violet-700 border border-violet-100 shadow-sm'
                      : 'text-slate-500 hover:text-violet-600 hover:bg-slate-50 border border-transparent'
                  }`}
                >
                  <span className={isActive ? 'text-violet-600' : 'text-slate-400 group-hover:text-violet-500'}>
                    {item.icon}
                  </span>
                  <span>{item.label}</span>
                </button>
              );
            })}

            {/* Submissions Dropdown */}
            <div className="relative">
              <button
                onClick={() => setSubmissionsDropdownOpen(!submissionsDropdownOpen)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-2 whitespace-nowrap ${
                  isSubmissionsActive
                    ? 'bg-violet-50 text-violet-700 border border-violet-100 shadow-sm'
                    : 'text-slate-500 hover:text-violet-600 hover:bg-slate-50 border border-transparent'
                }`}
              >
                <span className={isSubmissionsActive ? 'text-violet-600' : 'text-slate-400'}>
                  <MapPin className="w-4 h-4" />
                </span>
                <span>{isSubmissionsActive ? getActiveSubmissionLabel() : 'Sugerencias'}</span>
                <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${
                  submissionsDropdownOpen ? 'rotate-180' : ''
                } ${isSubmissionsActive ? 'text-violet-600' : 'text-slate-400'}`} />
              </button>

              {/* Submissions Dropdown Menu */}
              {submissionsDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-xl shadow-xl border border-slate-100 z-50 overflow-hidden ring-1 ring-black ring-opacity-5 animate-in slide-in-from-top-2 duration-200">
                  <div className="p-1.5">
                    {submissionsItems.map((item) => {
                      const isActive = activeTab === item.id;
                      return (
                        <button
                          key={item.id}
                          onClick={() => handleTabChange(item.id)}
                          className={`w-full flex items-center space-x-3 px-3 py-2.5 text-sm rounded-lg transition-all duration-200 ${
                            isActive
                              ? 'bg-violet-50 text-violet-700 font-medium shadow-sm'
                              : 'text-slate-600 hover:text-violet-600 hover:bg-slate-50'
                          }`}
                        >
                          <span className={isActive ? 'text-violet-600' : 'text-slate-400'}>
                            {item.icon}
                          </span>
                          <span>{item.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* User Section & Mobile Menu */}
          <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0 ml-auto">
            {/* User Info - Simplified on small screens */}
            <div className="hidden lg:block text-right text-sm">
              <div className="flex items-center justify-end space-x-1">
                <p className={`font-semibold whitespace-nowrap ${
                  loginType === 'master' ? 'text-red-800' : 'text-slate-700'
                }`}>
                  {email}
                </p>
              </div>
              <p className="text-slate-400 text-xs whitespace-nowrap">{getUserTypeLabel()}</p>
            </div>
            
            {/* User Dropdown */}
            <div className="relative">
              <button
                onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                disabled={loggingOut}
                className={`flex items-center space-x-1 sm:space-x-2 p-1.5 sm:p-2 rounded-lg transition-colors border border-transparent ${
                  loggingOut 
                    ? 'opacity-50 cursor-not-allowed' 
                    : loginType === 'master'
                      ? 'hover:bg-red-50 hover:border-red-100'
                      : 'hover:bg-slate-50 hover:border-slate-200'
                }`}
              >
                {getUserIcon()}
                <ChevronDown className={`h-3 w-3 sm:h-4 sm:w-4 transition-transform duration-200 ${
                  userDropdownOpen ? 'rotate-180' : ''
                } ${loginType === 'master' ? 'text-red-400' : 'text-slate-400'}`} />
              </button>

              {userDropdownOpen && !loggingOut && (
                <div className="absolute right-0 mt-2 w-60 bg-white rounded-xl shadow-xl border border-slate-100 z-50 overflow-hidden ring-1 ring-black ring-opacity-5">
                  <div className="p-4 border-b border-slate-50 bg-slate-50/50">
                    <div className="flex items-center space-x-3">
                      {loginType === 'master' ? (
                        <div className="p-2 bg-red-100 rounded-full"><Shield className="h-4 w-4 text-red-600" /></div>
                      ) : (
                        <div className="p-2 bg-violet-100 rounded-full"><User className="h-4 w-4 text-violet-600" /></div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-slate-800 truncate">{email}</p>
                        <p className="text-xs text-slate-500 truncate">{getUserTypeLabel()}</p>
                      </div>
                    </div>
                    {loginType === 'master' && (
                      <div className="mt-3 flex items-start space-x-2 text-xs bg-red-50 p-2 rounded text-red-700 border border-red-100">
                        <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                        <span>Privilegios de Super Admin activos</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="p-1.5">
                    <button
                      onClick={handleLogout}
                      className={`w-full flex items-center px-3 py-2.5 text-sm rounded-lg transition-colors group ${
                        loginType === 'master'
                          ? 'text-red-600 hover:bg-red-50'
                          : 'text-slate-600 hover:bg-slate-50 hover:text-violet-700'
                      }`}
                    >
                      <LogOut className={`h-4 w-4 mr-3 flex-shrink-0 transition-transform group-hover:-translate-x-0.5 ${
                        loginType === 'master' ? 'text-red-500' : 'text-slate-400 group-hover:text-violet-500'
                      }`} />
                      <span className="font-medium">Cerrar Sesión</span>
                    </button>
                  </div>
                  <div className="px-4 py-2 bg-slate-50 border-t border-slate-100">
                    <p className="text-[10px] text-center text-slate-400">Último acceso: {lastLogin}</p>
                  </div>
                </div>
              )}
              
              {loggingOut && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-slate-200 z-50 p-4">
                  <div className="flex items-center space-x-3 text-sm text-slate-600">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-violet-500 border-t-transparent flex-shrink-0"></div>
                    <span>Cerrando sesión...</span>
                  </div>
                </div>
              )}
            </div>

            {/* Mobile Menu Button - Shown on xl and below */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`xl:hidden p-2 rounded-lg transition-colors ${
                loginType === 'master' ? 'hover:bg-red-50 text-red-600' : 'hover:bg-slate-100 text-slate-600'
              }`}
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="xl:hidden py-3 border-t border-slate-100 animate-in slide-in-from-top-2 duration-200">
            <div className="space-y-1 px-1">
              {/* Main Nav Items */}
              {mainNavItems.map((item) => {
                 const isActive = activeTab === item.id;
                 return (
                  <button
                    key={item.id}
                    onClick={() => handleTabChange(item.id)}
                    className={`w-full text-left px-3 py-3 rounded-xl text-sm font-medium transition-all duration-200 flex items-center space-x-3 ${
                      isActive
                        ? 'bg-violet-50 text-violet-700 shadow-sm ring-1 ring-violet-100'
                        : 'text-slate-600 hover:text-violet-600 hover:bg-slate-50'
                    }`}
                  >
                    <span className={isActive ? 'text-violet-600' : 'text-slate-400'}>
                      {item.icon}
                    </span>
                    <span>{item.label}</span>
                  </button>
                 );
              })}

              {/* Submissions Section in Mobile */}
              <div className="pt-2 pb-1">
                <p className="px-3 text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                  Sugerencias
                </p>
                {submissionsItems.map((item) => {
                  const isActive = activeTab === item.id;
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleTabChange(item.id)}
                      className={`w-full text-left px-3 py-3 rounded-xl text-sm font-medium transition-all duration-200 flex items-center space-x-3 ${
                        isActive
                          ? 'bg-violet-50 text-violet-700 shadow-sm ring-1 ring-violet-100'
                          : 'text-slate-600 hover:text-violet-600 hover:bg-slate-50'
                      }`}
                    >
                      <span className={isActive ? 'text-violet-600' : 'text-slate-400'}>
                        {item.icon}
                      </span>
                      <span>{item.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Mobile User Info Summary */}
            <div className="mt-4 pt-4 border-t border-slate-100 px-4">
               <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">Cuenta Actual</p>
               <p className="text-sm font-medium text-slate-700">{email}</p>
               <p className="text-xs text-slate-500">{getUserTypeLabel()}</p>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}